// How a flow-edit approval card reads a node config value: references by the
// node they point at, lists by the rows that change. Pure, so the card only
// lays the results out.

import { isRecord } from "@/utils/isRecord";
import { formatValue, humanize, isEmptyValue, plural } from "./approvalValues";

// The name of the node a reference's handle (an id, a name or a build key)
// points at, when the flow knows it.
export type NodeNameOf = (handle: string) => string | undefined;

const VAR_REF = /:var\[([^\].]+)((?:\.[^\]]*)?)\]/g;
const NODE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

export function handleLabel(handle: string, nameOf: NodeNameOf): string {
  const name = nameOf(handle);
  if (name) return name;
  return NODE_ID.test(handle) ? `${handle.slice(0, 8)}…` : handle;
}

export function nameRefs(text: string, nameOf: NodeNameOf): string {
  return text.replace(
    VAR_REF,
    (_ref, handle: string, rest: string) =>
      `{${handleLabel(handle, nameOf)}${rest}}`
  );
}

function itemText(v: unknown, nameOf: NodeNameOf): string {
  if (typeof v === "string") return nameRefs(v, nameOf);
  if (Array.isArray(v)) return v.map((x) => itemText(x, nameOf)).join(", ");
  if (isRecord(v)) {
    return Object.entries(v)
      .filter(([, x]) => !isEmptyValue(x))
      .map(([k, x]) => `${humanize(k)}: ${itemText(x, nameOf)}`)
      .join(", ");
  }
  return formatValue(v) ?? "-";
}

// One line per row for a list of rows, the value itself otherwise.
export function valueLines(v: unknown, nameOf: NodeNameOf): string[] {
  if (isEmptyValue(v)) return ["-"];
  if (Array.isArray(v) && v.some(isRecord)) {
    return v.map((row) => itemText(row, nameOf));
  }
  return [itemText(v, nameOf)];
}

export type RowChange =
  | { kind: "added"; after: string }
  | { kind: "removed"; before: string }
  | { kind: "changed"; before: string; after: string }
  | { kind: "reordered"; before: string; after: string };

// The rows that differ between two lists: rows present in both are matched
// wherever they sit; the rest pair up in order as changed, and what is left
// over was added or removed. Order can change what a node does (List
// Operations' first item), so matched rows that moved are a change too, shown
// as the whole list before and after.
export function rowChanges(
  before: unknown[],
  after: unknown[],
  nameOf: NodeNameOf
): RowChange[] {
  const remaining = after.map((row) => JSON.stringify(row));
  const removed: unknown[] = [];
  const kept: string[] = [];
  for (const row of before) {
    const key = JSON.stringify(row);
    const at = remaining.indexOf(key);
    if (at === -1) removed.push(row);
    else {
      remaining[at] = "";
      kept.push(key);
    }
  }
  const added = after.filter((_, i) => remaining[i] !== "");
  const keptAfter = after
    .map((row) => JSON.stringify(row))
    .filter((key, i) => remaining[i] === "" && kept.includes(key));
  const text = (row: unknown) => itemText(row, nameOf);
  const list = (rows: unknown[]) => rows.map(text).join("; ");
  const paired = Math.min(removed.length, added.length);
  const moved = kept.some((key, i) => key !== keptAfter[i]);
  return [
    ...(moved
      ? [
          {
            kind: "reordered",
            before: list(before),
            after: list(after),
          } satisfies RowChange,
        ]
      : []),
    ...removed.slice(0, paired).map(
      (row, i): RowChange => ({
        kind: "changed",
        before: text(row),
        after: text(added[i]),
      })
    ),
    ...removed
      .slice(paired)
      .map((row): RowChange => ({ kind: "removed", before: text(row) })),
    ...added
      .slice(paired)
      .map((row): RowChange => ({ kind: "added", after: text(row) })),
  ];
}

export function rowChangesSummary(changes: RowChange[]): string {
  if (changes.length === 0) return "No change";
  const count = (kind: RowChange["kind"]) =>
    changes.filter((c) => c.kind === kind).length;
  const rows = (
    [
      ["changed", count("changed")],
      ["added", count("added")],
      ["removed", count("removed")],
    ] as const
  )
    .filter(([, n]) => n > 0)
    .map(([kind, n], i) =>
      i === 0 ? `${plural(n, "row")} ${kind}` : `${n} ${kind}`
    );
  if (count("reordered") === 0) return rows.join(", ");
  return rows.length === 0
    ? "Order changed"
    : [...rows, "order changed"].join(", ");
}
